<template>
  <div class="what-step min-h-full">
    <div class="flex items-center gap-2 mb-4">
      <h2 class="text-lg font-medium text-gray-900">WHAT - Entités associées</h2>
      <BookCheckIcon v-show="isStepValid" class="w-5 h-5 text-green-500" />
    </div>



    <div class="space-y-6" :class="{ 'opacity-90': readOnlyConfig?.isReadOnly }">
      <div class="form-group w-full">
        <label class="block mb-1">Objet d'étude:</label>
        <MaterialEntitySelector :user-id="userId" :initial-entity-uuid="modelValue.material_entity_uuid"
          :is-model-mode="modelValue.is_model" :material-entity="modelValue.materialEntity"
          :me-appellation="modelValue.me_appellation" :me-identifier="modelValue.me_identifier"
          :read-only-config="readOnlyConfig" @entity-selected="handleMaterialEntitySelected"
          @entity-reset="handleMaterialEntityReset" @update:me-appellation="handleMeAppellationUpdate"
          @update:me-identifier="handleMeIdentifierUpdate" />
      </div>

      <div v-if="selectedSchemaType !== 'Acquisition'" class="form-group w-full">
        <label for="digital_entity_input" class="block mb-1">Jeux de données d'entrée:</label>
        <DigitalEntitySelector :user-id="userId" :initial-entity-uuid="modelValue.digital_entity_input_id"
          :digital-entity="modelValue.digitalEntityInput" :new-entity-mode="modelValue.NewDigitalEntityCreated"
          :creation-only-mode="modelValue.NewDigitalEntityCreated" :is-model-mode="modelValue.is_model"
          :has-created-new-entity="modelValue.NewDigitalEntityCreated" :me-appellation="MEappellation"
          :me-identifier="MEidentifier" :read-only-config="readOnlyConfig"
          @entity-selected="handleDigitalEntityInputSelected" @entity-reset="handleDigitalEntitInputReset"
          @entity-created="handleDigitalEntityCreated" />
      </div>

      <div class="form-group w-full">
        <label for="digital_entity_output" class="block mb-1">
          Jeux de données de sortie:
          <span v-if="!readOnlyConfig?.isReadOnly" class="text-xs text-amber-600 ml-1">
            (Création obligatoire)
          </span>
        </label>
        <DigitalEntitySelector :user-id="userId" :initial-entity-uuid="modelValue.digital_entity_output_id"
          :digital-entity="modelValue.digitalEntityOutput" :new-entity-mode="false" :creation-only-mode="false"
          :is-model-mode="modelValue.is_model" :has-created-new-entity="false"
          :me-appellation="MEappellation" :me-identifier="MEidentifier"
          :read-only-config="readOnlyConfig" @entity-selected="handleDigitalEntityOutputSelected"
          @entity-reset="handleDigitalEntityOutputReset" @entity-created="handleDigitalEntityCreated" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { ReadOnlyConfig } from "@/types/readOnly";
import { BookCheckIcon } from 'lucide-vue-next';
import { defineComponent, reactive, ref, watch } from 'vue';
import type { DigitalEntity } from "../../api/types/entities/digitalEntity/types";
import type { MaterialEntity } from "../../api/types/entities/materialEntity/types";
import DigitalEntitySelector from '../selectors/DigitalEntitySelector.vue';
import MaterialEntitySelector from '../selectors/MaterialEntitySelector.vue';

export default defineComponent({
  name: 'WhatStep',
  components: {
    MaterialEntitySelector,
    DigitalEntitySelector,
    BookCheckIcon
  },
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    userId: {
      type: String,
      required: true
    },
    selectedSchemaType: {
      type: String,
      required: false,
      default: ''
    },
    readOnlyConfig: {
      type: Object as () => ReadOnlyConfig,
      required: false,
      default: () => ({ isReadOnly: false, allowNavigation: true, showEditHints: true })
    }
  },
  emits: ['update:model-value', 'isValid'],

  setup(props, { emit }) {

    // LOG DE DEBUG
    watch(() => props.modelValue, (newValue) => {
      //console.log('[WhatStep.vue] modelValue prop updated:', JSON.parse(JSON.stringify(newValue)));
      if (newValue.materialEntity) {
        //console.log('[WhatStep.vue] ✅ materialEntity is present:', JSON.parse(JSON.stringify(newValue.materialEntity)));
      } else {
        // console.log('[WhatStep.vue] ❌ materialEntity is MISSING.');
      }
    }, { deep: true, immediate: true });


    const MEappellation = ref('');
    const MEidentifier = ref('');

    const localData = reactive({
      digital_entity_input: props.modelValue.digital_entity_input || null,
      digital_entity_output: props.modelValue.digital_entity_output || null,
      NewDigitalEntityCreated: props.modelValue.NewDigitalEntityCreated || false
    });

    // Helper pour s'assurer qu'on passe toujours une string
    const getStringValue = (value: unknown): string => {
      if (typeof value === 'string') {
        return value;
      }
      if (value && typeof value === 'object' && 'uuid' in value) {
        return (value as { uuid: string }).uuid;
      }
      return '';
    };

    // Initialiser les valeurs de MEappellation et MEidentifier si une entité matérielle existe
    if (props.modelValue.materialEntity) {
      MEappellation.value = props.modelValue.materialEntity.appellation || '';
      MEidentifier.value = props.modelValue.materialEntity.identifier || '';
    }

    // Watcher pour les changements de l'entité matérielle
    watch(() => props.modelValue.materialEntity, (newEntity): void => {
      if (newEntity) {
        MEappellation.value = newEntity.appellation || '';
        MEidentifier.value = newEntity.identifier || '';
      } else {
        MEappellation.value = '';
        MEidentifier.value = '';
      }
    });

    const hasCreatedDigitalEntity = ref(props.modelValue.NewDigitalEntityCreated || false);

    // Watcher pour les changements de props.modelValue
    watch(() => props.modelValue, (newValue): void => {
      localData.NewDigitalEntityCreated = newValue.NewDigitalEntityCreated || false;
      hasCreatedDigitalEntity.value = newValue.NewDigitalEntityCreated || false;
      checkStepValidity();
    }, { deep: true });

    // Watcher pour les changements de props.selectedSchemaType
    watch(() => props.selectedSchemaType, (): void => {
      checkStepValidity();
    }, { deep: true });

    const isStepValid = ref(false);

    // Vérifier la validité de l'étape
    const checkStepValidity = (): void => {
      if (props.modelValue.is_model || false) {
        isStepValid.value = true;
      } else if (props.selectedSchemaType === 'Acquisition') {
        isStepValid.value = getStringValue(props.modelValue.material_entity_uuid) !== '';
      } else {
        isStepValid.value = getStringValue(props.modelValue.material_entity_uuid) !== '' &&
          hasCreatedDigitalEntity.value &&
          getStringValue(props.modelValue.digital_entity_output_id) !== '';
      }

      emit('isValid', isStepValid.value);
    };

    // Ajouter un watcher sur les valeurs qui peuvent affecter la validité
    watch([
      (): boolean => hasCreatedDigitalEntity.value,
      (): string => props.selectedSchemaType
    ], (): void => {
      checkStepValidity();
    }, { immediate: true });

    // Mettre à jour les données
    const updateData = (): void => {
      emit('update:model-value', {
        ...props.modelValue,
        NewDigitalEntityCreated: hasCreatedDigitalEntity.value
      });

      checkStepValidity();
    };

    const handleDigitalEntityCreated = (): void => {
      if (props.readOnlyConfig?.isReadOnly) return;

      hasCreatedDigitalEntity.value = true;
      emit('update:model-value', {
        ...props.modelValue,
        NewDigitalEntityCreated: true
      });
      updateData();
    };

    const handleDigitalEntityOutputReset = (): void => {
      if (props.readOnlyConfig?.isReadOnly) return;

      // La confirmation est maintenant gérée par le DigitalEntitySelector avec modal_areyousure
      localData.digital_entity_output = null;
      hasCreatedDigitalEntity.value = false;

      emit('update:model-value', {
        ...props.modelValue,
        NewDigitalEntityCreated: false,
        digital_entity_output_id: '',
        digitalEntityOutput: null
      });
      updateData();
    };

    const handleDigitalEntitInputReset = (): void => {
      if (props.readOnlyConfig?.isReadOnly) return;

      emit('update:model-value', {
        ...props.modelValue,
        digital_entity_input_id: '',
        digitalEntityInput: null
      });
      updateData();
    };

    const handleMaterialEntityReset = (): void => {
      if (props.readOnlyConfig?.isReadOnly) return;

      MEappellation.value = '';
      MEidentifier.value = '';

      emit('update:model-value', {
        ...props.modelValue,
        material_entity_uuid: '',
        materialEntity: null
      });
      updateData();
    };

    const handleMaterialEntitySelected = (data: { uuid: string, entity: MaterialEntity }): void => {
      if (props.readOnlyConfig?.isReadOnly) return;

      // Mettre à jour les valeurs locales pour les passer aux DigitalEntitySelector
      MEappellation.value = data.entity.appellation || '';
      MEidentifier.value = data.entity.identifier || '';

      emit('update:model-value', {
        ...props.modelValue,
        material_entity_uuid: data.uuid,
        materialEntity: data.entity
      });
      updateData();
    };

    const handleDigitalEntityInputSelected = (data: { uuid: string, entity?: DigitalEntity }): void => {
      if (props.readOnlyConfig?.isReadOnly) return;

      const uuid = typeof data === 'string' ? data : data.uuid;
      emit('update:model-value', {
        ...props.modelValue,
        digital_entity_input_id: uuid,
        digitalEntityInput: data.entity || null
      });
      updateData();
    };

    const handleDigitalEntityOutputSelected = (data: { uuid: string, entity?: DigitalEntity }): void => {
      if (props.readOnlyConfig?.isReadOnly) return;

      const uuid = typeof data === 'string' ? data : data.uuid;
      emit('update:model-value', {
        ...props.modelValue,
        digital_entity_output_id: uuid,
        digitalEntityOutput: data.entity || null
      });
      updateData();
    };

    const handleMeAppellationUpdate = (value: string): void => {
      if (props.readOnlyConfig?.isReadOnly) return;
      MEappellation.value = value;
    };

    const handleMeIdentifierUpdate = (value: string): void => {
      if (props.readOnlyConfig?.isReadOnly) return;
      MEidentifier.value = value;
    };

    return {
      localData,
      updateData,
      handleDigitalEntityCreated,
      handleDigitalEntityOutputReset,
      handleMaterialEntityReset,
      handleMaterialEntitySelected,
      handleDigitalEntitInputReset,
      handleDigitalEntityInputSelected,
      handleDigitalEntityOutputSelected,
      handleMeAppellationUpdate,
      handleMeIdentifierUpdate,
      MEappellation,
      MEidentifier,
      isStepValid,
      getStringValue
    };
  }
});
</script>